import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    ListSubheader,
    LinearProgress,
    makeStyles,
    MenuItem,
    Paper,
    Popover,
    Select,
    Typography
} from "@material-ui/core";
import BackupIcon from '@material-ui/icons/Backup';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from "react";
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import { useAppState } from "../context/zustand";
import { compareEpisodeNumbersDescending, getEpisodeNumber, sortEpisodesByEpisodeNumberDesc } from "../utils/episodeNumber";
import {
    EPISODE_MODE_ALL,
    EPISODE_MODE_NONE,
    getAvailableEpisodeIds,
    getEffectiveSelectedEpisodeIds,
    getEpisodeGroupSelectionState,
} from "../utils/episodeSelection";
import { useAxios } from "../utils/request";
import {
    formatIsoWeek,
    getDefaultCustomWeekInterval,
    getIntervalLabel,
    getWeekDisplayRange,
    getWeekEndDate,
    getWeekStartDate,
    normalizeWeekRange
} from "../utils/weekInterval";
import { BA_STYLE_TOKENS } from "../utils/utils";
import UploadFileModal from './modals/UploadModal';

const ALL_EPISODES_VALUE = '__all__';
const SEASON_VALUE_PREFIX = '__season__';
const NO_SEASON_KEY = '__no_season__';

const useStyles = makeStyles((theme) => ({
    root: {
        alignItems: 'center',
        border: '1px solid #e2e7ef',
        borderRadius: 6,
        boxShadow: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
        overflow: 'hidden',
        padding: theme.spacing(1.5, 2),
        position: 'relative',
    },
    channelGroup: {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1.25),
        minWidth: 0,
    },
    channelOption: {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    channelOptionImage: {
        border: '1px solid #e2e7ef',
        borderRadius: 5,
        flex: '0 0 auto',
        height: 28,
        objectFit: 'cover',
        width: 28,
    },
    channelOptionText: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    channelSelect: {
        minWidth: 260,
        maxWidth: 360,
    },
    borderlessSelect: {
        color: '#101828',
        fontWeight: 700,
        minHeight: 38,
        '&:before': {
            display: 'none',
        },
        '&:after': {
            display: 'none',
        },
        '&:hover:not(.Mui-disabled):before': {
            display: 'none',
        },
        '& .MuiSelect-select': {
            paddingBottom: theme.spacing(0.75),
            paddingLeft: 0,
            paddingTop: theme.spacing(0.75),
        },
    },
    staticChannel: {
        color: '#101828',
        fontWeight: 700,
        maxWidth: 320,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    sectionMenu: {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1),
    },
    sectionButton: {
        color: BA_STYLE_TOKENS.brand,
        fontWeight: 700,
        borderBottom: '2px solid transparent',
        borderRadius: 0,
        minHeight: 38,
        padding: theme.spacing(0.75, 0),
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
            borderBottomColor: BA_STYLE_TOKENS.brand,
        },
    },
    selectedSectionButton: {
        borderBottomColor: BA_STYLE_TOKENS.brand,
    },
    inactiveSectionButton: {
        color: '#667085',
        cursor: 'default',
        fontWeight: 700,
        borderBottom: '2px solid transparent',
        borderRadius: 0,
        minHeight: 38,
        opacity: 0.7,
        padding: theme.spacing(0.75, 0),
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
            borderBottomColor: '#98a2b3',
        },
    },
    spacer: {
        flex: '1 1 auto',
    },
    episodeSelect: {
        minWidth: 240,
        maxWidth: 420,
    },
    menuItemProgress: {
        color: BA_STYLE_TOKENS.brand,
        marginLeft: theme.spacing(1),
    },
    seasonMenuItem: {
        alignItems: 'center',
        color: '#101828',
        cursor: 'pointer',
        display: 'flex',
        fontWeight: 700,
        lineHeight: 1.5,
        minHeight: 40,
        outline: 'none',
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    seasonListSubheader: {
        padding: 0,
    },
    episodeMenuItem: {
        paddingLeft: theme.spacing(4),
    },
    seasonCount: {
        color: '#667085',
        fontSize: 12,
        marginLeft: theme.spacing(1),
    },
    episodeMenuPaper: {
        maxHeight: 420,
    },
    selectionProgress: {
        backgroundColor: '#d8e3fb',
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        '& .MuiLinearProgress-bar': {
            backgroundColor: BA_STYLE_TOKENS.brand,
        },
    },
    intervalButton: {
        backgroundColor: BA_STYLE_TOKENS.brand,
        color: '#ffffff',
        flex: '0 0 250px',
        minHeight: 40,
        width: 250,
        whiteSpace: 'nowrap',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#163174',
        },
    },
    popoverContent: {
        padding: theme.spacing(2),
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
    },
    targetButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1.5),
    },
    targetButton: {
        minWidth: 140,
    },
    intervalModeButtons: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'center',
        marginBottom: theme.spacing(1.5),
    },
    presetButtons: {
        display: 'grid',
        gap: theme.spacing(1),
        gridTemplateColumns: '1fr',
    },
    presetButton: {
        justifyContent: 'center',
        minHeight: 40,
        textTransform: 'none',
    },
    selectedRangeText: {
        marginBottom: theme.spacing(1),
        textAlign: 'center',
    },
    pickerNav: {
        alignItems: 'center',
        display: 'grid',
        gridTemplateColumns: '40px 1fr 40px',
        gap: theme.spacing(1),
        margin: theme.spacing(1, 0, 0.5),
        width: '100%',
    },
    pickerNavButton: {
        color: BA_STYLE_TOKENS.brand,
        minWidth: 32,
        padding: 4,
    },
    pickerNavLabel: {
        color: '#101828',
        fontWeight: 700,
        justifySelf: 'center',
        textAlign: 'center',
    },
    pickerActions: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
        marginTop: theme.spacing(1.5),
    },
    uploadButton: {
        color: BA_STYLE_TOKENS.brand,
        fontWeight: 700,
        borderBottom: '2px solid transparent',
        borderRadius: 0,
        minHeight: 38,
        padding: theme.spacing(0.75, 0),
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
            borderBottomColor: BA_STYLE_TOKENS.brand,
        },
    },
}));

function getEpisodeDisplayNumber(episode, podcastName) {
    const episodeNumber = episode?.episode_number;

    if (episodeNumber !== null && episodeNumber !== undefined && episodeNumber !== '') {
        return String(episodeNumber);
    }

    return getEpisodeNumber(episode?.id, podcastName);
}

function getEpisodeLabel(episode, podcastName) {
    if (!episode) return '';
    const episodeNumber = getEpisodeDisplayNumber(episode, podcastName);
    return episode.name ? `${episodeNumber}: ${episode.name}` : episodeNumber;
}

function getEpisodeSelectLabel(mode, selectedIds, episodeCount) {
    if (episodeCount === 0) return 'No episodes available';
    if (mode === EPISODE_MODE_ALL) return 'All episodes';
    if (mode === EPISODE_MODE_NONE) return 'No episodes selected';
    return `${selectedIds.length} episodes selected`;
}

function getWeekDays(weekValue) {
    const start = getWeekStartDate(weekValue);
    return Array.from({ length: 7 }, (_, index) => moment(start).add(index, 'days').toDate());
}

function ChannelOption({ podcast, classes }) {
    return (
        <Box className={classes.channelOption}>
            <img className={classes.channelOptionImage} src={podcast.artwork_url} alt="" />
            <span className={classes.channelOptionText}>{`${podcast.name}: ${podcast.full_name}`}</span>
        </Box>
    );
}

function CalendarNavbar({
    month,
    onPreviousClick,
    onNextClick,
    showPreviousButton,
    showNextButton,
    labels,
    classes,
}) {
    return (
        <Box className={classes.pickerNav}>
            <Button
                aria-label={labels?.previousMonth || 'Previous month'}
                className={classes.pickerNavButton}
                disabled={!showPreviousButton}
                onClick={() => onPreviousClick()}
            >
                <KeyboardArrowLeftIcon />
            </Button>
            <Typography className={classes.pickerNavLabel}>
                {moment(month).format('MMMM YYYY')}
            </Typography>
            <Button
                aria-label={labels?.nextMonth || 'Next month'}
                className={classes.pickerNavButton}
                disabled={!showNextButton}
                onClick={() => onNextClick()}
            >
                <KeyboardArrowRightIcon />
            </Button>
        </Box>
    );
}

function EmptyCalendarCaption() {
    return null;
}

function hasSeasonNumber(episode) {
    return episode?.season_number !== null && episode?.season_number !== undefined && episode?.season_number !== '';
}

function getSeasonKey(episode) {
    return hasSeasonNumber(episode) ? String(episode.season_number) : NO_SEASON_KEY;
}

function getSeasonValue(seasonKey) {
    return `${SEASON_VALUE_PREFIX}${seasonKey}`;
}

function getSeasonLabel(seasonKey) {
    return seasonKey === NO_SEASON_KEY ? 'No season' : `Season ${seasonKey}`;
}

function getEpisodeNumberSortData(episode, podcastName) {
    const value = getEpisodeDisplayNumber(episode, podcastName);
    if (!/^\d/.test(String(value))) {
        return { value, numeric: null };
    }

    const numeric = parseInt(value, 10);
    return {
        value,
        numeric: Number.isFinite(numeric) ? numeric : null,
    };
}

function compareEpisodesWithinSeason(a, b, podcastName) {
    const aNumber = getEpisodeNumberSortData(a, podcastName);
    const bNumber = getEpisodeNumberSortData(b, podcastName);

    if (aNumber.numeric !== null && bNumber.numeric !== null && aNumber.numeric !== bNumber.numeric) {
        return bNumber.numeric - aNumber.numeric;
    }

    if (aNumber.numeric !== null && bNumber.numeric === null) {
        return -1;
    }

    if (aNumber.numeric === null && bNumber.numeric !== null) {
        return 1;
    }

    return compareEpisodeNumbersDescending(a.id, b.id, podcastName);
}

function buildSeasonGroups(episodes, podcastName) {
    const grouped = (episodes || []).reduce((acc, episode) => {
        const seasonKey = getSeasonKey(episode);
        if (!acc[seasonKey]) {
            acc[seasonKey] = [];
        }
        acc[seasonKey].push(episode);
        return acc;
    }, {});

    return Object.entries(grouped)
        .map(([seasonKey, items]) => ({
            seasonKey,
            seasonValue: getSeasonValue(seasonKey),
            label: getSeasonLabel(seasonKey),
            episodes: [...items].sort((a, b) => compareEpisodesWithinSeason(a, b, podcastName)),
        }))
        .sort((a, b) => {
            if (a.seasonKey === NO_SEASON_KEY) return 1;
            if (b.seasonKey === NO_SEASON_KEY) return -1;
            return Number(b.seasonKey) - Number(a.seasonKey);
        });
}

function WeekIntervalPopover({ anchorEl, onClose }) {
    const classes = useStyles();
    const intervalStartWeek = useAppState(s => s.intervalStartWeek);
    const intervalEndWeek = useAppState(s => s.intervalEndWeek);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const setIntervalPreset = useAppState(s => s.setIntervalPreset);
    const setIntervalWeeks = useAppState(s => s.setIntervalWeeks);
    const customDefault = getDefaultCustomWeekInterval();
    const [selectionStep, setSelectionStep] = useState('presets');
    const [draftStartWeek, setDraftStartWeek] = useState(intervalStartWeek || customDefault.intervalStartWeek);
    const [draftEndWeek, setDraftEndWeek] = useState(intervalEndWeek || customDefault.intervalEndWeek);
    const [activeTarget, setActiveTarget] = useState('start');
    const [hoverRange, setHoverRange] = useState(undefined);

    useEffect(() => {
        if (anchorEl) {
            setSelectionStep(intervalPreset === 'custom' ? 'custom' : 'presets');
            setDraftStartWeek(intervalStartWeek || customDefault.intervalStartWeek);
            setDraftEndWeek(intervalEndWeek || customDefault.intervalEndWeek);
            setActiveTarget('start');
        }
        // eslint-disable-next-line
    }, [anchorEl, intervalStartWeek, intervalEndWeek, intervalPreset]);

    const normalizedDraft = normalizeWeekRange(draftStartWeek, draftEndWeek);
    const draftRange = getWeekDisplayRange(normalizedDraft.intervalStartWeek, normalizedDraft.intervalEndWeek);
    const selectedDays = [
        ...getWeekDays(normalizedDraft.intervalStartWeek),
        ...getWeekDays(normalizedDraft.intervalEndWeek)
    ];

    const handleDaySelect = (day) => {
        const week = formatIsoWeek(day);
        if (activeTarget === 'start') {
            setDraftStartWeek(week);
            return;
        }
        setDraftEndWeek(week);
    };

    const handleApply = () => {
        setIntervalWeeks({ startWeek: draftStartWeek, endWeek: draftEndWeek });
        onClose();
    };

    const handlePresetClick = (preset) => {
        setIntervalPreset(preset);
        onClose();
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Box className={classes.popoverContent}>
                <Box className={classes.intervalModeButtons}>
                    <Button
                        color="primary"
                        variant={selectionStep === 'presets' ? 'contained' : 'outlined'}
                        onClick={() => setSelectionStep('presets')}
                    >
                        Presets
                    </Button>
                    <Button
                        color="primary"
                        variant={selectionStep === 'custom' ? 'contained' : 'outlined'}
                        onClick={() => setSelectionStep('custom')}
                    >
                        Custom
                    </Button>
                </Box>
                {selectionStep === 'presets' ? (
                    <Box className={classes.presetButtons}>
                        <Button className={classes.presetButton} color="primary" variant="outlined" onClick={() => handlePresetClick('all_time')}>
                            All time
                        </Button>
                        <Button className={classes.presetButton} color="primary" variant="outlined" onClick={() => handlePresetClick('last_month')}>
                            Last month
                        </Button>
                        <Button className={classes.presetButton} color="primary" variant="outlined" onClick={() => handlePresetClick('last_3_months')}>
                            Last 3 months
                        </Button>
                        <Button className={classes.presetButton} color="primary" variant="outlined" onClick={() => handlePresetClick('last_year')}>
                            Last year
                        </Button>
                        <Button className={classes.presetButton} color="primary" variant="outlined" onClick={() => handlePresetClick('this_year')}>
                            This year
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Box className={classes.targetButtons}>
                            <Button
                                className={classes.targetButton}
                                color="primary"
                                variant={activeTarget === 'start' ? 'contained' : 'outlined'}
                                onClick={() => setActiveTarget('start')}
                            >
                                Start week
                            </Button>
                            <Button
                                className={classes.targetButton}
                                color="primary"
                                variant={activeTarget === 'end' ? 'contained' : 'outlined'}
                                onClick={() => setActiveTarget('end')}
                            >
                                End week
                            </Button>
                        </Box>
                        <Typography className={classes.selectedRangeText} variant="body2">
                            {draftRange.intervalStartDate} - {draftRange.intervalEndDate}
                        </Typography>
                        <DayPicker
                            className="SelectionWeekPicker"
                            captionElement={<EmptyCalendarCaption />}
                            firstDayOfWeek={1}
                            navbarElement={<CalendarNavbar classes={classes} />}
                            showOutsideDays
                            showWeekNumbers
                            selectedDays={selectedDays}
                            modifiers={{
                                hoverRange,
                                selectedRange: {
                                    from: getWeekStartDate(normalizedDraft.intervalStartWeek).toDate(),
                                    to: getWeekEndDate(normalizedDraft.intervalEndWeek).toDate(),
                                },
                                selectedRangeStart: getWeekStartDate(normalizedDraft.intervalStartWeek).toDate(),
                                selectedRangeEnd: getWeekEndDate(normalizedDraft.intervalEndWeek).toDate(),
                            }}
                            modifiersStyles={{
                                hoverRange: {
                                    backgroundColor: '#d8e3fb',
                                    borderRadius: 0,
                                },
                                selectedRange: {
                                    backgroundColor: '#d8e3fb',
                                    borderRadius: 0,
                                    color: '#101828',
                                },
                                selectedRangeStart: {
                                    backgroundColor: BA_STYLE_TOKENS.brand,
                                    borderRadius: 0,
                                    color: '#ffffff',
                                },
                                selectedRangeEnd: {
                                    backgroundColor: BA_STYLE_TOKENS.brand,
                                    borderRadius: 0,
                                    color: '#ffffff',
                                },
                            }}
                            onDayClick={handleDaySelect}
                            onDayMouseEnter={(day) => {
                                const week = formatIsoWeek(day);
                                setHoverRange({
                                    from: getWeekStartDate(week).toDate(),
                                    to: getWeekEndDate(week).toDate(),
                                });
                            }}
                            onDayMouseLeave={() => setHoverRange(undefined)}
                        />
                        <style>{`
                            .DayPicker.SelectionWeekPicker {
                                display: block;
                                width: 100%;
                            }
                            .SelectionWeekPicker .DayPicker-wrapper {
                                box-sizing: border-box;
                                padding-bottom: 0;
                                width: 100%;
                            }
                            .SelectionWeekPicker .DayPicker-Months {
                                display: flex;
                                justify-content: center;
                                width: 100%;
                            }
                            .SelectionWeekPicker .DayPicker-Month {
                                margin: 0 auto;
                            }
                            .SelectionWeekPicker .DayPicker-Caption {
                                display: none;
                            }
                            .SelectionWeekPicker .DayPicker-NavBar {
                                position: static;
                            }
                        `}</style>
                        <Box className={classes.pickerActions}>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button color="primary" variant="contained" onClick={handleApply}>Apply</Button>
                        </Box>
                    </>
                )}
            </Box>
        </Popover>
    );
}

export default function SelectionBar() {
    const classes = useStyles();
    const history = useHistory();
    const params = useParams();
    const podcast = useAppState(s => s.podcast);
    const podcasts = useAppState(s => s.podcasts);
    const activeSection = useAppState(s => s.activeSection);
    const isAdmin = useAppState(s => s.isAdmin);
    const episodeMode = useAppState(s => s.episodeMode);
    const selectedEpisodeIds = useAppState(s => s.selectedEpisodeIds);
    const episodeSelectionBusy = useAppState(s => s.episodeSelectionBusy);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const intervalStartDate = useAppState(s => s.intervalStartDate);
    const intervalEndDate = useAppState(s => s.intervalEndDate);
    const setPodcast = useAppState(s => s.setPodcast);
    const setPodcasts = useAppState(s => s.setPodcasts);
    const setSharedEpisodes = useAppState(s => s.setEpisodes);
    const clearCollectedWeekRange = useAppState(s => s.clearCollectedWeekRange);
    const setActiveSection = useAppState(s => s.setActiveSection);
    const applyEpisodeSelection = useAppState(s => s.applyEpisodeSelection);
    const reconcileEpisodeSelection = useAppState(s => s.reconcileEpisodeSelection);
    const [episodes, setEpisodes] = useState([]);
    const [intervalAnchor, setIntervalAnchor] = useState(null);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const episodeGroups = useMemo(() => (
        buildSeasonGroups(episodes, podcast?.name)
    ), [episodes, podcast?.name]);
    const availableEpisodeIds = useMemo(() => getAvailableEpisodeIds(episodes), [episodes]);
    const selection = useMemo(() => ({
        mode: episodeMode,
        selectedEpisodeIds,
    }), [episodeMode, selectedEpisodeIds]);
    const effectiveSelectedEpisodeIds = useMemo(() => (
        getEffectiveSelectedEpisodeIds(selection, availableEpisodeIds)
    ), [selection, availableEpisodeIds]);
    const effectiveSelectedEpisodeIdSet = useMemo(() => (
        new Set(effectiveSelectedEpisodeIds)
    ), [effectiveSelectedEpisodeIds]);
    const allSelectionState = useMemo(() => (
        getEpisodeGroupSelectionState(selection, availableEpisodeIds, availableEpisodeIds)
    ), [selection, availableEpisodeIds]);
    const busyTarget = episodeSelectionBusy?.target;
    const isEpisodeSelectionBusy = Boolean(episodeSelectionBusy);

    useAxios(
        ["podcasts"],
        {
            url: "/api/podcasts/",
            method: "GET"
        },
        { enabled: true },
        (res) => {
            const nextPodcasts = res.data.results;
            setPodcasts(nextPodcasts);
            if (nextPodcasts.length === 0) {
                setPodcast(null);
                return;
            }
            if (!params?.podcast) {
                history.replace(`/${nextPodcasts[0].name}`);
            }
        }
    );

    const episodesRequest = useAxios(
        ["selection-episodes", podcast?.name],
        {
            method: 'get',
            url: '/api/episodes/',
            params: {
                ordering: '-release_date',
                podcast__name: podcast?.name
            }
        },
        { enabled: Boolean(podcast?.name) },
        () => {},
        () => {}
    );

    useEffect(() => {
        if (episodesRequest.isError) {
            setEpisodes([]);
            setSharedEpisodes([]);
            return;
        }

        if (episodesRequest.data) {
            const sortedEpisodes = sortEpisodesByEpisodeNumberDesc(episodesRequest.data.data.results, podcast?.name);
            setEpisodes(sortedEpisodes);
            setSharedEpisodes(sortedEpisodes);
            reconcileEpisodeSelection(getAvailableEpisodeIds(sortedEpisodes));
        }
        // The active React Query observer changes with the podcast key, so an
        // older podcast response cannot update this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [episodesRequest.data, episodesRequest.isError, podcast?.name]);

    useEffect(() => {
        setEpisodes([]);
        setSharedEpisodes([]);
        clearCollectedWeekRange();
        // eslint-disable-next-line
    }, [podcast?.name]);

    useEffect(() => {
        if (podcasts.length === 0) {
            return;
        }
        const selected = podcasts.find(item => item.name === params?.podcast) || podcasts[0];
        if (selected?.name && selected.name !== params?.podcast) {
            history.replace(`/${selected.name}`);
            return;
        }
        if (selected?.name !== podcast?.name) {
            setPodcast(selected);
        }
        // eslint-disable-next-line
    }, [params?.podcast, podcasts, podcast?.name]);

    const handlePodcastChange = (event) => {
        const selected = podcasts.find((item) => item.name === event.target.value);
        if (selected) {
            history.push(`/${selected.name}`);
        }
    };

    const getSeasonSelectionState = (group) => getEpisodeGroupSelectionState(
        selection,
        group.episodes.map((episode) => episode.id),
        availableEpisodeIds
    );

    const getSeasonAriaLabel = (group) => {
        const groupState = getSeasonSelectionState(group);
        const state = groupState.checked
            ? 'selected'
            : (groupState.indeterminate ? 'partially selected' : 'not selected');
        return `${group.label}, ${groupState.selectedCount} of ${groupState.totalCount} episodes selected, ${state}`;
    };

    const applySelectionAction = (action, target) => {
        applyEpisodeSelection({ action, availableEpisodeIds, target });
    };

    const handleSelectionPointerDown = (event, action, target) => {
        event.preventDefault();
        event.stopPropagation();
        applySelectionAction(action, target);
    };

    const handleSelectionKeyDown = (event, action, target) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        applySelectionAction(action, target);
    };

    if (!podcast?.name) {
        return null;
    }

    return (
        <Paper className={classes.root}>
            <Box className={classes.channelGroup}>
                {podcasts.length > 1 ? (
                    <FormControl size="small" className={classes.channelSelect}>
                        <Select
                            className={classes.borderlessSelect}
                            disableUnderline
                            value={podcast.name}
                            onChange={handlePodcastChange}
                            renderValue={(selectedName) => {
                                const selectedPodcast = podcasts.find((item) => item.name === selectedName) || podcast;
                                return <ChannelOption podcast={selectedPodcast} classes={classes} />;
                            }}
                        >
                            {podcasts.map((item) => (
                                <MenuItem key={`selection-podcast-${item.name}`} value={item.name}>
                                    <ChannelOption podcast={item} classes={classes} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Typography className={classes.staticChannel}>
                        {`${podcast.name}: ${podcast.full_name}`}
                    </Typography>
                )}
            </Box>
            <Box className={classes.sectionMenu}>
                <Button
                    className={`${classes.sectionButton} ${activeSection === 'graphs' ? classes.selectedSectionButton : ''}`}
                    color="primary"
                    onClick={() => setActiveSection('graphs')}
                    variant="text"
                >
                    Graphs
                </Button>
                <Button
                    className={`${classes.sectionButton} ${activeSection === 'episodes' ? classes.selectedSectionButton : ''}`}
                    color="primary"
                    onClick={() => setActiveSection('episodes')}
                    variant="text"
                >
                    Episodes
                </Button>
            </Box>
            <Box className={classes.spacer} />
            <FormControl size="small" className={classes.episodeSelect}>
                <Select
                    className={classes.borderlessSelect}
                    disableUnderline
                    displayEmpty
                    multiple
                    value={episodeMode === EPISODE_MODE_ALL ? [ALL_EPISODES_VALUE] : selectedEpisodeIds.map(String)}
                    onChange={() => {}}
                    renderValue={() => getEpisodeSelectLabel(episodeMode, selectedEpisodeIds, episodes.length)}
                    disabled={episodes.length === 0}
                    MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                        PaperProps: {
                            className: classes.episodeMenuPaper,
                        },
                    }}
                >
                    <MenuItem
                        value={ALL_EPISODES_VALUE}
                        aria-checked={allSelectionState.indeterminate ? 'mixed' : allSelectionState.checked}
                        aria-label={`All, ${allSelectionState.selectedCount} of ${allSelectionState.totalCount} episodes selected`}
                        onKeyDown={(event) => handleSelectionKeyDown(event, { type: 'all' }, ALL_EPISODES_VALUE)}
                        onPointerDown={(event) => handleSelectionPointerDown(event, { type: 'all' }, ALL_EPISODES_VALUE)}
                        role="checkbox"
                    >
                        <Checkbox
                            color="primary"
                            checked={allSelectionState.checked}
                            indeterminate={allSelectionState.indeterminate}
                            inputProps={{ 'aria-hidden': true }}
                            tabIndex={-1}
                        />
                        All
                        <span className={classes.seasonCount}>{`${episodes.length} episodes`}</span>
                        {busyTarget === ALL_EPISODES_VALUE &&
                            <CircularProgress className={classes.menuItemProgress} size={14} />
                        }
                    </MenuItem>
                    {episodeGroups.map((group) => (
                        <React.Fragment key={`selection-season-${group.seasonKey}`}>
                            <ListSubheader className={classes.seasonListSubheader} disableSticky>
                                <Box
                                    className={classes.seasonMenuItem}
                                    aria-label={getSeasonAriaLabel(group)}
                                    aria-checked={getSeasonSelectionState(group).indeterminate ? 'mixed' : getSeasonSelectionState(group).checked}
                                    onKeyDown={(event) => handleSelectionKeyDown(event, {
                                        type: 'season',
                                        episodeIds: group.episodes.map((episode) => episode.id),
                                    }, group.seasonValue)}
                                    onPointerDown={(event) => handleSelectionPointerDown(event, {
                                        type: 'season',
                                        episodeIds: group.episodes.map((episode) => episode.id),
                                    }, group.seasonValue)}
                                    role="checkbox"
                                    tabIndex={0}
                                >
                                    <Checkbox
                                        color="primary"
                                        checked={getSeasonSelectionState(group).checked}
                                        indeterminate={getSeasonSelectionState(group).indeterminate}
                                        inputProps={{ 'aria-hidden': true }}
                                        tabIndex={-1}
                                    />
                                    {group.label}
                                    <span className={classes.seasonCount}>{`${group.episodes.length} episodes`}</span>
                                    {busyTarget === group.seasonValue &&
                                        <CircularProgress className={classes.menuItemProgress} size={14} />
                                    }
                                </Box>
                            </ListSubheader>
                            {group.episodes.map((episode) => {
                                const episodeId = String(episode.id);

                                return (
                                    <MenuItem
                                        className={classes.episodeMenuItem}
                                        key={`selection-episode-${episode.id}`}
                                        value={episodeId}
                                        aria-checked={effectiveSelectedEpisodeIdSet.has(episodeId)}
                                        aria-label={`${getEpisodeLabel(episode, podcast?.name)}, ${effectiveSelectedEpisodeIdSet.has(episodeId) ? 'selected' : 'not selected'}`}
                                        onKeyDown={(event) => handleSelectionKeyDown(event, {
                                            type: 'episode',
                                            episodeId,
                                        }, episodeId)}
                                        onPointerDown={(event) => handleSelectionPointerDown(event, {
                                            type: 'episode',
                                            episodeId,
                                        }, episodeId)}
                                        role="checkbox"
                                    >
                                        <Checkbox
                                            color="primary"
                                            checked={effectiveSelectedEpisodeIdSet.has(episodeId)}
                                            inputProps={{ 'aria-hidden': true }}
                                            tabIndex={-1}
                                        />
                                        {getEpisodeLabel(episode, podcast?.name)}
                                        {busyTarget === episodeId &&
                                            <CircularProgress className={classes.menuItemProgress} size={14} />
                                        }
                                    </MenuItem>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </Select>
            </FormControl>
            {isEpisodeSelectionBusy &&
                <LinearProgress className={classes.selectionProgress} />
            }
            {isAdmin &&
                <Button
                    className={classes.uploadButton}
                    startIcon={<BackupIcon />}
                    onClick={() => setOpenUploadModal(true)}
                >
                    Upload file
                </Button>
            }
            <Button
                className={classes.intervalButton}
                variant="contained"
                onClick={(event) => setIntervalAnchor(event.currentTarget)}
            >
                {getIntervalLabel({ intervalPreset, intervalStartDate, intervalEndDate })}
            </Button>
            <WeekIntervalPopover
                anchorEl={intervalAnchor}
                onClose={() => setIntervalAnchor(null)}
            />
            {
                openUploadModal &&
                <UploadFileModal
                    open={openUploadModal}
                    podcast={podcast}
                    onRssComplete={() => episodesRequest.refetch()}
                    handleClose={() => {
                        setOpenUploadModal(false);
                    }}
                />
            }
        </Paper>
    );
}
